<i18n>
{
  "fr": {
    "separator": ", "
  }
}
</i18n>

<template>
  <BaseDropdown
    v-model="isExpanded"
    :class="{
      [`${BLOCK_SELECTOR}--disabled`]: disabled,
      [`${BLOCK_SELECTOR}--expanded`]: isExpanded,
    }"
    class="custom-select"
    fit-parent
    fit-viewport
    v-bind="{
      modal,
      modalBreakpoint,
      modalOptions,
      modalTargetSlot,
      modalClass,
    }"
  >
    <!-- Trigger -->
    <template #trigger="{ toggleState }">
      <span class="custom-select__label-area">
        <span aria-hidden="true" class="custom-select__hidden-options">
          <CustomSelectOptionList
            v-bind="{
              name,
              options,
              optionValueProp,
              optionLabelProp,
              value: computedValue,
              multiple,
              disabled,
            }"
          >
            <template #default="scope">
              <slot v-bind="scope" name="option"></slot>
            </template>
          </CustomSelectOptionList>
        </span>

        <span v-if="isValueVisible" class="custom-select__value">
          <slot
            name="value"
            v-bind="{ options, value: computedValue, optionMap }"
          >
            <template v-if="!multiple && $scopedSlots.option">
              <slot :option="valueOption" name="option"></slot>
            </template>
            <template v-else>
              {{ valueDefaultRender }}
            </template>
          </slot>
        </span>

        <span v-else-if="$slots.placeholder" class="custom-select__placeholder">
          <slot name="placeholder"></slot>
        </span>
      </span>

      <button
        :aria-controls="dropdownId"
        :aria-expanded="isExpanded.toString()"
        :aria-labelledby="labelledBy"
        aria-haspopup="listbox"
        :disabled="disabled"
        class="custom-select__btn-toggle"
        type="button"
        @click="toggleState"
      >
        <slot name="btn-toggle"></slot>
      </button>
    </template>

    <!-- Target -->
    <template #target="{ toggleState }">
      <CustomSelectOptionList
        v-model="computedValue"
        class="custom-select__target"
        v-bind="{
          name,
          options,
          optionValueProp,
          optionLabelProp,
          multiple,
          disabled,
          class: targetClass,
        }"
        v-on="getOptionEvents(toggleState)"
      >
        <template #default="scope">
          <slot v-bind="scope" name="option"></slot>
        </template>
      </CustomSelectOptionList>
    </template>
  </BaseDropdown>
</template>

<script>
import BaseDropdown from './BaseDropdown';
import CustomSelectOptionList from './CustomSelectOptionList';
import Select from '../mixins/Select';
import { randomStr } from '../utils/String';
import SelectOptionProps from '../mixins/SelectOptionProps';
import BaseDropdownModalProps from '../mixins/BaseDropdownModalProps';

const BLOCK_SELECTOR = 'custom-select';

export default {
  name: 'CustomSelect',
  components: { CustomSelectOptionList, BaseDropdown },
  mixins: [Select, SelectOptionProps, BaseDropdownModalProps],
  props: {
    targetClass: {
      type: [String, Array, Object],
      default: null,
    },
  },
  data() {
    return {
      isExpanded: this.expandedByDefault,
      localValue: this.value,
      BLOCK_SELECTOR,
    };
  },
  computed: {
    dropdownId() {
      return `${BLOCK_SELECTOR}__dropdown--${this.name}--${randomStr()}`;
    },
    computedValue: {
      get() {
        return this.localValue;
      },
      set(v) {
        this.localValue = v;

        this.$emit('change', v);
      },
    },
    valueDefaultRender() {
      const valueOptions = this.valueOption;

      if (valueOptions instanceof Array) {
        return valueOptions
          .map(o => this.getOptionLabel(o))
          .join(this.$t('separator'));
      } else {
        return valueOptions && this.getOptionLabel(valueOptions);
      }
    },
    valueOption() {
      if (this.multiple) {
        const optionMap = this.optionMap;

        return this.computedValue.map(v => optionMap[v]);
      } else {
        return this.optionMap[this.computedValue];
      }
    },
    optionMap() {
      return this.options.reduce((acc, o) => {
        acc[this.getOptionValue(o)] = o;

        return acc;
      }, {});
    },
    isValueVisible() {
      if (this.multiple) {
        return this.computedValue && this.computedValue.length;
      } else {
        return this.computedValue;
      }
    },
  },
  watch: {
    value: {
      handler(v) {
        this.localValue = v;
      },
      immediate: true,
    },
  },
  methods: {
    /**
     *
     * @param {Function} toggleCb
     * @return {Object}
     */
    getOptionEvents(toggleCb) {
      const o = {};

      if (!this.multiple) {
        if (!(toggleCb instanceof Function)) {
          throw new TypeError('The provided callback is invalid.');
        }

        o.change = toggleCb;
      }

      return o;
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-select {
  $block-selector: &;
  display: inline-flex;
  user-select: none;
  overflow: hidden;
  position: relative;

  &--disabled {
    cursor: not-allowed;
  }

  &__label-area {
    flex: 1;
    min-width: 0;
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: 100%;
  }

  &__placeholder,
  &__value {
    grid-column: 1 / span 1;
    grid-row: 1 / span 1;
    padding: 0.5em;
    align-self: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow-x: hidden;
    color: $color-gray-dove;
  }

  &__hidden-options {
    grid-column: 1 / span 1;
    grid-row: 1 / span 1;
    visibility: hidden;
    overflow: hidden;
    height: 1px;
  }

  &__btn-toggle {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25em;
    border: 0;
    border-radius: 0;
    background: none;
    color: inherit;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }

    &:focus {
      outline: none;
    }

    &::before {
      @extend %icon;
      @extend %icon--arrow-fill-bottom;
      display: block;
      font-size: 0.75em;
      transition: transform $default-transition-duration;

      #{$block-selector}--expanded & {
        transform: rotate(180deg);
      }
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
    }
  }

  &__target {
    .base-dropdown__target & {
      background: $color-default-background;
    }
  }
}
</style>
